import { create } from 'zustand';
import { noteTargetKey, type NoteMention, type NoteTarget } from '@shared/flow/notes';
import type { ContactSearchResult } from '@shared/flow/search';

/**
 * Module-scope Notes state (zustand, never a React context; D6/D8). One store
 * for the open thread, the "posting to" destination, per-target drafts, recent
 * targets and the in-flight post. Reset with
 * `useFlowNotesStore.setState(initialState, true)` or `reset()`.
 */

/** Which people's notes the account thread shows. */
export type NotesPeopleFilter = 'all' | 'account' | 'contacts' | { contactId: string };

/** The one in-flight (or just-failed) post, kept so the thread can show it. */
export interface NotesPending {
  clientNoteId: string;
  target: NoteTarget;
  body: string;
  /** ISO timestamp of when the rep pressed post. */
  at: string;
  status: 'saving' | 'awaiting' | 'failed' | 'unchanged';
  error?: { title: string; oauthUrl?: string };
  /** What was posted, so `retry()` can re-send the identical request. */
  mentions?: NoteMention[];
  source?: 'composer' | 'save_as_note';
  /**
   * Entries the posted-to record had in the cached thread before the post, or
   * null when no thread was cached. Confirmation needs the count to GROW so an
   * identical historical line never confirms a new post.
   */
  baselineCount?: number | null;
  /** All confirmation re-checks ran without a match ("Not confirmed — Refresh"). */
  notConfirmed?: boolean;
}

/** Most targets kept in `recents`. */
export const NOTES_RECENTS_MAX = 8;

export interface FlowNotesState {
  target: NoteTarget | null;
  peopleFilter: NotesPeopleFilter;
  postingTo: NoteTarget | null;
  /** Keyed by `noteTargetKey`. */
  drafts: Record<string, string>;
  draftMentions: Record<string, NoteMention[]>;
  /** Label of the auto-inserted leading `@Label ` on a draft key (null/absent: none), so a destination switch can tell it from rep-typed text. */
  draftPrefix: Record<string, string | null>;
  /** Most recent first, de-duped by key, capped at `NOTES_RECENTS_MAX`. */
  recents: NoteTarget[];
  /**
   * The search rows of contacts opened from record search, by contact id. Feeds
   * the search's own "Recent" section and the names on the start screen.
   */
  contactRows: Record<string, ContactSearchResult>;
  pending: NotesPending | null;
  /** The last post Salesforce history confirmed; `useMarkRead` re-marks the thread read on change. */
  lastConfirmed: { clientNoteId: string; target: NoteTarget } | null;
  /**
   * Opens a target. `prefill` lands only on an empty draft; `filter` (chips
   * pass `'all'`, D10) is applied only when given, and the filter always
   * resets to `'all'` when the account changes; `postingTo` always resets.
   */
  openTarget: (target: NoteTarget, opts?: { prefill?: string; filter?: 'all' }) => void;
  /**
   * Fills in the account id of a record that was opened without one (the mirror lacked it and the live
   * read found it). Touches only entries for that same record that still have no account id.
   */
  adoptAccountId: (record: Pick<NoteTarget, 'kind' | 'id'>, accountId: string) => void;
  setPeopleFilter: (filter: NotesPeopleFilter) => void;
  setPostingTo: (target: NoteTarget | null) => void;
  setDraft: (targetKey: string, text: string) => void;
  clearDraft: (targetKey: string) => void;
  setDraftMentions: (targetKey: string, mentions: NoteMention[]) => void;
  setDraftPrefix: (targetKey: string, prefix: string | null) => void;
  pushRecent: (target: NoteTarget) => void;
  rememberContactRow: (row: ContactSearchResult) => void;
  setPending: (pending: NotesPending | null) => void;
  /** Back to `initialState` (identity change must not leak drafts/recents/pending). */
  reset: () => void;
}

type FlowNotesData = Pick<
  FlowNotesState,
  'target' | 'peopleFilter' | 'postingTo' | 'drafts' | 'draftMentions' | 'draftPrefix' | 'recents' | 'contactRows' | 'pending' | 'lastConfirmed'
>;

const initialData: FlowNotesData = {
  target: null,
  peopleFilter: 'all',
  postingTo: null,
  drafts: {},
  draftMentions: {},
  draftPrefix: {},
  recents: [],
  contactRows: {},
  pending: null,
  lastConfirmed: null,
};

function withRecent(recents: NoteTarget[], target: NoteTarget): NoteTarget[] {
  const key = noteTargetKey(target);
  return [target, ...recents.filter(r => noteTargetKey(r) !== key)].slice(0, NOTES_RECENTS_MAX);
}

// The actions go through the store's own `setState`/`getState` (resolved
// lazily at call time) so they can live in `initialState`: the repo's reset
// idiom `setState(initialState, true)` REPLACES the whole state, and the
// actions must survive it.
const actions: Omit<FlowNotesState, keyof FlowNotesData> = {
  openTarget: (target, opts) => {
    const key = noteTargetKey(target);
    const { drafts, recents, target: current } = useFlowNotesStore.getState();
    const prefill = opts?.prefill;
    useFlowNotesStore.setState({
      target,
      postingTo: target,
      recents: withRecent(recents, target),
      ...(opts?.filter
        ? { peopleFilter: opts.filter }
        : current?.accountId !== target.accountId ? { peopleFilter: 'all' as const } : {}),
      ...(prefill !== undefined && !drafts[key] ? { drafts: { ...drafts, [key]: prefill } } : {}),
    });
  },
  adoptAccountId: (record, accountId) => {
    const fill = (t: NoteTarget): NoteTarget =>
      t.kind === record.kind && t.id === record.id && t.accountId === null ? { ...t, accountId } : t;
    useFlowNotesStore.setState(s => ({
      target: s.target ? fill(s.target) : s.target,
      postingTo: s.postingTo ? fill(s.postingTo) : s.postingTo,
      recents: s.recents.map(fill),
    }));
  },
  setPeopleFilter: (peopleFilter) => useFlowNotesStore.setState({ peopleFilter }),
  setPostingTo: (postingTo) => useFlowNotesStore.setState({ postingTo }),
  setDraft: (targetKey, text) =>
    useFlowNotesStore.setState(s => ({ drafts: { ...s.drafts, [targetKey]: text } })),
  clearDraft: (targetKey) =>
    useFlowNotesStore.setState(s => {
      const { [targetKey]: _draft, ...drafts } = s.drafts;
      const { [targetKey]: _mentions, ...draftMentions } = s.draftMentions;
      const { [targetKey]: _prefix, ...draftPrefix } = s.draftPrefix;
      return { drafts, draftMentions, draftPrefix };
    }),
  setDraftMentions: (targetKey, mentions) =>
    useFlowNotesStore.setState(s => ({ draftMentions: { ...s.draftMentions, [targetKey]: mentions } })),
  setDraftPrefix: (targetKey, prefix) =>
    useFlowNotesStore.setState(s => ({ draftPrefix: { ...s.draftPrefix, [targetKey]: prefix } })),
  pushRecent: (target) =>
    useFlowNotesStore.setState(s => ({ recents: withRecent(s.recents, target) })),
  rememberContactRow: (row) =>
    useFlowNotesStore.setState(s => ({ contactRows: { ...s.contactRows, [row.id]: row } })),
  setPending: (pending) => useFlowNotesStore.setState({ pending }),
  reset: () => useFlowNotesStore.setState(initialState, true),
};

/** Data AND actions: `setState(initialState, true)` must leave a working store. */
export const initialState: FlowNotesState = { ...initialData, ...actions };

export const useFlowNotesStore = create<FlowNotesState>(() => initialState);
